import React, { useMemo, useState, useEffect, useRef } from "react";
import SettingsIcon from "@mui/icons-material/Settings";
import { IconButton, Switch } from "@mui/material";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { checkPermissionBasedOnFeedId } from "orion-components/Profiles/Selectors";
import {
	Video,
	Export,
	Bookmark,
	JumpTime
} from "orion-components/CBComponents/Icons";
import { getTranslation } from "orion-components/i18n";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { makeStyles } from "@mui/styles";
import JumpToDifferentTime from "./JumpToDifferentTime/JumpToDifferentTime";
import EventBus from "orion-components/SharedComponents/EventBus";
import { getActualDuration, calcuateDateTimeDifference } from "../../utils";
import moment from "moment";
import AddBookmarkDialog from "./AddBookmarkDialog";
import AddClipDialog from "./AddClipDialog";
import ExportClipDialog from "./ExportClipDialog";

const useStyles = makeStyles({
	thumbOff: {
		backgroundColor: "#ffffff"
	},
	trackOff: {
		backgroundColor: "#828283",
		opacity: 1
	},
	thumbSwitched: {
		backgroundColor: "#29B6F6"
	},
	trackSwitched: {
		backgroundColor: "#bee1f1!important",
		opacity: "1!important"
	}
});

const SettingsMenu = ({ duration, formatedDuration, formatedDurationUnit, instanceId, toggleSettingsMenu, setVideoDurationExternally, setStartDateExternally, startDateTime, cameraId, feedId, currentTime, dialogContainerRef }) => {
	const classes = useStyles();

	const [dateTime, setDateTime] = useState(null);
	const dateTimeDuration = getActualDuration(duration);
	const startDateValue = calcuateDateTimeDifference(dateTimeDuration.value, dateTimeDuration.type, dateTime);

	const [displayMenu, setDisplayMenu] = useState(null);
	const [toggleControls, setToggleControls] = useState(true);
	const dir = useSelector(state => getDir(state));
	const canManageCameraFeed = useSelector(state =>
		checkPermissionBasedOnFeedId(state)(feedId, "permissions", "manage")
	);
	const [displayJumpToTimeMenu, setDisplayJumpToTimeMenu] = useState(false);
	const [displayAddBookmarkDialog, setDisplayAddBookmarkDialog] = useState(false);
	const [displayAddClipDialog, setDisplayAddClipDialog] = useState(false);
	const [displayExportClipDialog, setDisplayExportClipDialog] = useState(false);

	const jumpToTimeRef = useRef(null);

	const styles = useMemo(() => {
		return {
			menuIcons: {
				...(dir === "ltr" && { paddingLeft: 4 }),
				...(dir === "ltr" && { paddingRight: 4 })
			}
		};
	}, [dir]);

	const menuItems = [
		{
			id: 1,
			canAccessMenu: true,
			item: toggleControls
				? getTranslation("global.CBComponents.newVideoPlayer.components.recorded.settingsMenu.showAlarms")
				: getTranslation("global.CBComponents.newVideoPlayer.components.recorded.settingsMenu.hideAlarms"),
			icon: toggleControls ? (
				<VisibilityIcon sx={{ width: 18.66, height: 18.66, color: "#4DB5F4" }} />
			) : (
				<VisibilityOffIcon sx={{ width: 18.66, height: 18.66, color: "#948E8E" }} />
			),
			action: () => {
				setToggleControls(!toggleControls);
				EventBus.publish("toggleAlertVisiblityOnTimeLine", { instanceId, value: !toggleControls });
			},
			hasNestedMenu: false,
			hasSwitch: true
		},
		{
			id: 2,
			item: "divider"
		},
		{
			id: 3,
			canAccessMenu: true,
			item: getTranslation("global.CBComponents.newVideoPlayer.components.recorded.settingsMenu.jump"),
			icon: (
				<div style={styles.menuIcons}>
					<JumpTime />
				</div>
			),
			action: () => {
				setDisplayJumpToTimeMenu(false);
				setDisplayJumpToTimeMenu(true);
			}
		},
		{
			id: 4,
			canAccessMenu: canManageCameraFeed,
			item: getTranslation("global.CBComponents.newVideoPlayer.components.recorded.settingsMenu.saveClip"),
			icon: (
				<div style={styles.menuIcons}>
					<Video />
				</div>
			),
			action: () => {
				setDisplayAddClipDialog(true);
				setDisplayMenu(null);
				toggleSettingsMenu(false);
			}
		},
		{
			id: 5,
			canAccessMenu: canManageCameraFeed,
			item: getTranslation("global.CBComponents.newVideoPlayer.components.recorded.settingsMenu.saveBookMark"),
			icon: (
				<div style={styles.menuIcons}>
					<Bookmark />
				</div>
			),
			action: () => {
				setDisplayAddBookmarkDialog(true);
				setDisplayMenu(null);
				toggleSettingsMenu(false);
			}
		},
		{
			id: 6,
			canAccessMenu: canManageCameraFeed,
			item: getTranslation("global.CBComponents.newVideoPlayer.components.recorded.settingsMenu.export"),
			icon: (
				<div style={styles.menuIcons}>
					<Export />
				</div>
			),
			action: () => {
				setDisplayExportClipDialog(true);
				setDisplayMenu(null);
				toggleSettingsMenu(false);
			}
		}
	];

	const handlePopoverClose = () => {
		setDisplayMenu(null);
		if (!displayJumpToTimeMenu) {
			toggleSettingsMenu(false);
		}
	};

	useEffect(() => {
		if (startDateTime) setDateTime(startDateTime);
		else setDateTime(moment().subtract(0, "minutes"));
	}, [startDateTime]);

	useEffect(() => {
		if (displayJumpToTimeMenu && jumpToTimeRef.current) {
			jumpToTimeRef.current.getTime(dateTime);
		}
	}, [displayJumpToTimeMenu, dateTime]);

	return (
		<div dir={dir} style={{ zIndex: 1 }}>
			<div style={{ width: 16.2, height: "100%" }}>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						height: "100%"
					}}
				>
					<IconButton
						sx={{ height: 16.2 }}
						onClick={() => {
							toggleSettingsMenu(true);
							setDisplayMenu(true);
						}} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-settings-menu-icon-button"
					>
						<SettingsIcon sx={{ color: "#FFFFFF" }} />
					</IconButton>
				</div>
			</div>
			{displayJumpToTimeMenu && (
				<JumpToDifferentTime
					open={displayJumpToTimeMenu}
					instanceId={instanceId}
					closePopup={() => {
						setDisplayJumpToTimeMenu(false);
					}}
					position="right"
					isDateTimeFor={"startDate"}
					setVideoDurationExternally={setVideoDurationExternally}
					setStartDateExternally={setStartDateExternally}
					ref={jumpToTimeRef}
					formatedDuration={formatedDuration}
					formatedDurationUnit={formatedDurationUnit}
				/>
			)}
			<AddBookmarkDialog
				open={displayAddBookmarkDialog}
				cameraId={cameraId}
				timestamp={currentTime}
				container={dialogContainerRef?.current ?? null}
				onClose={() => {
					setDisplayAddBookmarkDialog(false);
				}} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-settings-menu-add-bookmark-dialog"
			/>
			<AddClipDialog
				open={displayAddClipDialog}
				cameraId={cameraId}
				timestamp={currentTime}
				container={dialogContainerRef?.current ?? null}
				onClose={() => {
					setDisplayAddClipDialog(false);
				}} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-settings-menu-add-clip-dialog"
			/>
			<ExportClipDialog
				open={displayExportClipDialog}
				cameraId={cameraId}
				timestamp={currentTime}
				container={dialogContainerRef?.current ?? null}
				onClose={() => {
					setDisplayExportClipDialog(false);
				}} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-settings-menu-export-clip-dialog"
			/>
			<div
				style={{
					display: displayMenu !== null ? "block" : "none",
					position: "absolute",
					bottom: 0,
					...(dir === "ltr" && { right: 15 }),
					...(dir === "rtl" && { left: 15 }),
					color: "#fff",
					zIndex: 10
				}}
				onMouseLeave={handlePopoverClose} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-settings-menu-div"
			>
				<div
					style={{
						width: 160,
						minHeight: 120,
						maxHeight: 220,
						overflowY: "auto",
						overflowX: "hidden",
						backgroundColor: "#232223",
						borderRadius: 6,
						boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
						padding: "4px 0",
						overscrollBehavior: "contain"
					}}
					onWheel={event => event.stopPropagation()} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-settings-menu-div-2"
				>
					{menuItems.map((value, index) => {
						if (value.item !== "divider") {
							if (value.canAccessMenu) {
								return (
									<div
										key={index}
										className="bcc-main-menu"
										onClick={value.action}
										style={{
											display: "flex",
											flexDirection: "row",
											alignItems: "center",
											minHeight: 20,
											padding: "2px 5px",
											cursor: "pointer",
											fontSize: 10,
											lineHeight: "12px",
											gap: 12
										}} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-settings-menu-div-3"
									>
										<div style={{ flex: "0 0 24px", display: "flex", alignItems: "center" }}>
											{value.icon}
										</div>
										<div style={{ flex: "1 1 auto", display: "flex", alignItems: "center" }}>
											<span>{value.item}</span>
										</div>
										{value.hasSwitch && (
											<IconButton sx={{ marginLeft: "auto", padding: 0 }} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-settings-menu-icon-button-2">
												<Switch
													size="small"
													edge="end"
													onChange={value.action}
													checked={toggleControls}
													classes={{
														thumb: toggleControls
															? classes.thumbSwitched
															: classes.thumbOff,
														track: toggleControls ? classes.trackSwitched : classes.trackOff
													}} data-testid="orion-components-cbcomponents-new-video-player-components-recorded-settings-menu-switch"
												/>
											</IconButton>
										)}
									</div>
								);
							}
						} else {
							return (
								<div
									key={index}
									style={{
										width: "90%",
										height: 1,
										backgroundColor: "#444",
										margin: "8px auto"
									}}
								></div>
							);
						}
					})}
				</div>
			</div>
		</div>
	);
};

const defaultProps = {
	toggleSettingsMenu: () => { }
};

SettingsMenu.propTypes = {
	duration: PropTypes.number,
	formatedDuration: PropTypes.number,
	formatedDurationUnit: PropTypes.string,
	instanceId: PropTypes.string,
	cameraId: PropTypes.string,
	feedId: PropTypes.string,
	currentTime: PropTypes.instanceOf(Date),
	dialogContainerRef: PropTypes.shape({
		current: PropTypes.any
	}),
	startDateTime: PropTypes.any,
	toggleSettingsMenu: PropTypes.func,
	setVideoDurationExternally: PropTypes.func,
	setStartDateExternally: PropTypes.func
};
SettingsMenu.defaultProps = defaultProps;

export default SettingsMenu;
